import { api } from '@/api/endpoints';
import { logQueueRepo } from '@/data/repositories/logQueueRepo';
import { connectivity } from '@/services/connectivity';
import type { LogEntry } from '@/types/domain';
import { createLogger } from '@/lib/logger';
import { clientId } from '@/lib/utils';
import { stampUploadFailure, stampUploadSuccess } from '@/lib/config';
import type { LogType } from '@/types/domain';

const log = createLogger('logQueue');

interface EnqueueArgs {
  logType: LogType | string;
  display?: string;
  assignmentId?: string;
  tag?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  additionalFields?: Record<string, string | number | null>;
}

let flushInflight: Promise<void> | null = null;

// FileMaker's log time (log_L03_time) has SECOND precision, and workflow
// actions enqueue several logs back to back (e.g. FINISH UNLOADING then
// DEL DEPARTED within milliseconds). Colliding timestamps made FM's
// date/time sort order those rows arbitrarily. Keep capturedAt strictly
// increasing at second granularity so the enqueue sequence always survives
// into FileMaker's sort (DEL DEPARTED sorts after FINISH UNLOADING, etc.).
let lastCapturedSecond = 0;

function nextCapturedAt(): string {
  const nowSecond = Math.floor(Date.now() / 1000);
  const second = nowSecond <= lastCapturedSecond ? lastCapturedSecond + 1 : nowSecond;
  lastCapturedSecond = second;
  return new Date(second * 1000).toISOString();
}

export const logQueueService = {
  /**
   * Add a log entry to the queue and trigger a (best-effort) flush.
   */
  async enqueue(args: EnqueueArgs): Promise<LogEntry> {
    const row = await logQueueRepo.enqueue({
      logType: args.logType,
      display: args.display,
      assignmentId: args.assignmentId,
      tag: args.tag,
      latitude: args.latitude,
      longitude: args.longitude,
      accuracy: args.accuracy,
      capturedAt: nextCapturedAt(),
      additionalFields: args.additionalFields,
    });
    void this.flush();
    return row;
  },

  /**
   * Push everything currently pending to the middleware, marking each
   * row sent / failed accordingly. The single-flight guard ensures we
   * never run multiple concurrent flushes from different triggers
   * (visibility change, timer, manual button, etc.).
   */
  async flush(): Promise<void> {
    if (flushInflight) return flushInflight;
    if (!connectivity.online) return;

    flushInflight = (async () => {
      try {
        const batch = await logQueueRepo.pendingBatch(25);
        if (!batch.length) return;
        await logQueueRepo.markInflight(batch.map((b) => b.id));

        try {
          const resp = await api.pushLogBatch(batch);
          // Guard the response shape: mid-deploy or behind a broken proxy the
          // request can "succeed" with a body that is not a batch result, and
          // the raw TypeError ("undefined is not an object") is undiagnosable
          // on the admin board.
          if (!resp || !Array.isArray(resp.results)) {
            throw new Error('Server returned an unexpected response to the log batch (not a batch result; possibly a proxy error page or mid-deploy state)');
          }
          const byClientId = new Map(
            resp.results.map((r) => [r.clientId, r] as const),
          );
          let anySent = false;
          for (const entry of batch) {
            const result = byClientId.get(entry.id);
            if (result?.success) {
              anySent = true;
              await logQueueRepo.markSent(entry.id, result.fmRecordId);
            } else {
              const error = result?.error ?? 'unknown error';
              stampUploadFailure(error);
              await logQueueRepo.markFailed(entry.id, error);
            }
          }
          if (anySent) stampUploadSuccess();
        } catch (err) {
          log.warn('batch flush failed', err);
          const error = (err as Error).message ?? String(err);
          stampUploadFailure(error);
          for (const entry of batch) {
            await logQueueRepo.markFailed(entry.id, error);
          }
        }
      } finally {
        flushInflight = null;
      }
    })();

    return flushInflight;
  },

  /**
   * Internal helper used by tests to assemble a row id consistently.
   */
  newId: clientId,
};
